<template>
  <transition name="strip-slide">
    <div
      v-if="visible"
      class="announcement-strip"
      role="alert"
      aria-live="polite"
    >
      <div class="strip-inner">
        <!-- Left glow accent -->
        <span class="strip-glow" aria-hidden="true"></span>

        <!-- Content -->
        <div class="strip-content">
          <span class="strip-emoji" aria-hidden="true">🚀</span>
          <span class="strip-message">{{ message }}</span>
        </div>

        <!-- Actions -->
        <div class="strip-actions">
          <button
            class="btn btn-outline-primary"
            @click="handleTryNew"
            type="button"
          >
            {{ ctaLabel }}
          </button>
          <!-- <button
            class="strip-btn strip-btn--ghost"
            @click="handleFeedback"
            type="button"
          >
            Give Feedback
          </button> -->
        </div>

        <!-- Close -->
        <button
          class="strip-close"
          @click="dismiss"
          type="button"
          aria-label="Dismiss announcement"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'AnnouncementStrip',

  props: {
    message: {
      type: String,
      default: "We're working on a new dashboard. Try it out and share your feedback!"
    },
    ctaLabel: {
      type: String,
      default: 'Try New Dashboard'
    },
    ctaUrl: {
      type: String,
      default: '/analytics/beta'
    },
    dismissKey: {
      type: String,
      default: 'hide_dashboard_strip'
    }
  },

  data() {
    return {
      visible: false
    };
  },

  mounted() {
    const hidden = localStorage.getItem(this.dismissKey);
    if (!hidden) {
      // Small tick delay so the slide-down transition fires on load
      this.$nextTick(() => {
        setTimeout(() => { this.visible = true; }, 80);
      });
    }
  },

  methods: {
    handleTryNew() {
      this.$emit('onTryNew');
      this.track('strip_cta_click');
      window.location.href = this.ctaUrl;
    },

    handleFeedback() {
      this.$emit('onFeedbackClick');
      this.track('give_feedback');
    },

    dismiss() {
      this.visible = false;
      localStorage.setItem(this.dismissKey, '1');
      this.$emit('dismissed');
    },

    /**
     * Lightweight analytics hook.
     * Swap body for your real tracker (Segment, Mixpanel, GTM dataLayer, etc.)
     */
    track(eventName) {
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', eventName, { event_category: 'announcement_strip' });
        } else if (window.dataLayer) {
          window.dataLayer.push({ event: eventName, category: 'announcement_strip' });
        } else {
          console.info('[AnnouncementStrip] track:', eventName);
        }
      } catch (e) {
        // never crash the UI over analytics
      }
    }
  }
};
</script>

<style scoped>
/* ─── Strip container ─────────────────────────────────────────── */
.announcement-strip {
  position: sticky; /* fixed would overlay page content once it wraps to 2+ lines on mobile */
  top: 0;
  left: 0;
  right: 0;
  z-index: 1035; /* above the navbar (1030), below modal backdrops (1040+) so modals/sidebars dim it */

  /*
   * UX note on opacity:
   * rgba(253, 71, 25, 0.08) is the sweet spot.
   *   • 0.04–0.06 → too invisible; strip can blend into light navbars
   *   • 0.08–0.10 → perceptible warmth that signals "pay attention"
   *                  without screaming; text contrast stays high (~7:1)
   *   • 0.15+     → starts feeling intrusive / alert-like
   * A 1px solid border at rgba(253,71,25,0.25) adds definition cheaply.
   */
  background: rgba(253, 71, 25, 0.08);
  border-bottom: 1px solid rgba(253, 71, 25, 0.22);

  /* Frosted feel on modern browsers */
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

/* ─── Inner layout ────────────────────────────────────────────── */
.strip-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px 16px;
  padding: 9px 48px 9px 20px; /* right pad reserves space for the X */
  position: relative;
  max-width: 1400px;
  margin: 0 auto;
}

/* Left accent glow – purely decorative */
.strip-glow {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #fd4719;
  border-radius: 0 2px 2px 0;
  opacity: 0.7;
}

/* ─── Message ─────────────────────────────────────────────────── */
.strip-content {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 1;
  min-width: 0;
}

.strip-emoji {
  font-size: 15px;
  flex-shrink: 0;
  line-height: 1;
}

.strip-message {
  font-size: 12.5px;
  font-weight: 500;
  color: #1a1a1a;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ─── Action buttons ──────────────────────────────────────────── */
.strip-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.strip-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
  border-radius: 5px;
  padding: 5px 12px;
  height: 28px;
  transition: opacity 0.15s ease, transform 0.1s ease, box-shadow 0.15s ease;
  white-space: nowrap;
  line-height: 1;
}

.strip-btn:focus {
  outline: 2px solid #fd4719;
  outline-offset: 2px;
}

.strip-btn:active {
  transform: translateY(1px);
}

/* Primary – solid brand */
.strip-btn--primary {
  background: #fd4719;
  color: #fff;
  box-shadow: 0 1px 4px rgba(253, 71, 25, 0.35);
}

.strip-btn--primary:hover {
  opacity: 0.9;
  box-shadow: 0 2px 8px rgba(253, 71, 25, 0.45);
}

/* Ghost – outlined */
.strip-btn--ghost {
  background: transparent;
  color: #fd4719;
  border: 1.5px solid rgba(253, 71, 25, 0.45);
}

.strip-btn--ghost:hover {
  background: rgba(253, 71, 25, 0.06);
  border-color: rgba(253, 71, 25, 0.7);
}

/* ─── Close button ────────────────────────────────────────────── */
.strip-close {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 5px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s ease, background 0.15s ease;
  flex-shrink: 0;
}

.strip-close:hover {
  color: #fd4719;
  background: rgba(253, 71, 25, 0.08);
}

.strip-close:focus {
  outline: 2px solid #fd4719;
  outline-offset: 2px;
}

/* ─── Slide-down transition ───────────────────────────────────── */
.strip-slide-enter-active {
  transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1),
              opacity   0.28s ease;
}

.strip-slide-leave-active {
  transition: transform 0.22s ease-in,
              opacity   0.18s ease-in;
}

.strip-slide-enter,
.strip-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

/* ─── Responsive ──────────────────────────────────────────────── */
@media (max-width: 767px) {
  .strip-inner {
    padding: 10px 44px 10px 14px;
    justify-content: flex-start;
    gap: 8px 12px;
  }

  .strip-message {
    white-space: normal;
    font-size: 12px;
  }

  .strip-btn {
    font-size: 11px;
    padding: 5px 10px;
    height: 26px;
  }
}

@media (max-width: 480px) {
  .strip-actions {
    flex-wrap: wrap;
    gap: 6px;
  }
}
</style>
